import React, { useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, DollarSign, QrCode, LogOut } from 'lucide-react';

import { cn } from '../../lib/utils';
import { PAGE_BG, useBrandBodyBackground } from '../referral/brandTheme';
import { usePortal } from '../../contexts/PortalContext';
import { usePortalLogout } from '../../services/portal';

// Partner Portal shell — dark premium brand, ADAPTIVE navigation:
//  - desktop (md+): slim top nav with inline links
//  - mobile: fixed bottom tab bar in the thumb zone (44px+ targets,
//    safe-area-inset padding); content gets bottom padding so nothing hides.
// PWA-lite: while mounted, the document points at the portal manifest +
// theme-color so add-to-home-screen installs the portal standalone. Restored
// on unmount (same pattern as useBrandBodyBackground).

const TABS = [
  { to: '/portal', label: 'Home', icon: Home, end: true },
  { to: '/portal/referrals', label: 'Referrals', icon: Users },
  { to: '/portal/payouts', label: 'Payouts', icon: DollarSign },
  { to: '/portal/link', label: 'My Link', icon: QrCode },
];

export const usePortalManifest = () => {
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"]');
    const createdLink = !link;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    const prevHref = link.getAttribute('href');
    link.setAttribute('href', '/portal.webmanifest');

    let meta = document.querySelector('meta[name="theme-color"]');
    const createdMeta = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    const prevColor = meta.getAttribute('content');
    // Matches the black header chrome (not PAGE_BG) so the phone status bar
    // blends into the top rail in standalone/PWA mode.
    meta.setAttribute('content', '#000000');

    return () => {
      if (createdLink) link.remove();
      else if (prevHref) link.setAttribute('href', prevHref);
      if (createdMeta) meta.remove();
      else if (prevColor) meta.setAttribute('content', prevColor);
    };
  }, []);
};

const PortalLayout = ({ children, hideNav = false }) => {
  useBrandBodyBackground();
  usePortalManifest();
  const navigate = useNavigate();
  const location = useLocation();

  // Each page owns its layout instance, so React Router preserves scroll across
  // tab switches — snap to the top on navigation like a native app would.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const { portalUser, portalSignOut } = usePortal();
  const logout = usePortalLogout();

  const signOut = async () => {
    // Server revocation is best-effort — clearing the local session must never
    // depend on the network.
    try {
      await logout.mutateAsync();
    } catch (e) {
      /* already signed out server-side or offline — proceed */
    }
    portalSignOut();
    navigate('/portal/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col text-white" style={{ backgroundColor: PAGE_BG }}>
      {/* Top bar: wordmark + desktop nav + sign out. Sticky so the brand and
          exits stay reachable in long lists. Black chrome (header + mobile tab
          bar): the grey content plane floats between two anchored black rails. */}
      <header
        className="sticky top-0 z-40 border-b border-white/10 backdrop-blur"
        style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
      >
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/VR-Name-White-Horizontal-01.png"
              alt="Veritas Ratings"
              className="h-6 w-auto"
            />
            <span className="hidden h-5 w-0.5 bg-white/20 sm:inline-block" aria-hidden="true" />
            <span className="hidden text-xs font-medium text-neutral-400 sm:inline">
              Partner Portal
            </span>
          </div>
          {!hideNav ? (
            <nav className="hidden items-center gap-1 md:flex">
              {TABS.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  className={({ isActive }) =>
                    cn(
                      // no-underline (incl. hover) fights the legacy Bootstrap
                      // anchor styles that still load app-wide on this branch.
                      'rounded-md px-3 py-2 text-sm font-medium no-underline transition-colors hover:no-underline',
                      isActive
                        ? 'bg-white/10 text-white'
                        : 'text-neutral-400 hover:bg-white/5 hover:text-white',
                    )
                  }
                >
                  {t.label}
                </NavLink>
              ))}
            </nav>
          ) : null}
          <div className="flex items-center gap-3">
            {portalUser?.displayName ? (
              <span className="hidden max-w-[160px] truncate text-xs text-neutral-400 sm:inline">
                {portalUser.displayName}
              </span>
            ) : null}
            <button
              type="button"
              onClick={signOut}
              title="Sign out"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2 text-xs font-medium text-neutral-400 transition-colors hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* pb-24 clears the mobile tab bar; desktop needs less. */}
      <main className={cn('mx-auto w-full max-w-5xl flex-1 px-4 pt-5', hideNav ? 'pb-10' : 'pb-24 md:pb-10')}>
        {children}
      </main>

      {!hideNav ? (
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 backdrop-blur md:hidden"
          style={{
            backgroundColor: 'rgba(0,0,0,0.95)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          aria-label="Portal navigation"
        >
          <div className="mx-auto flex max-w-5xl">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium no-underline transition-colors hover:no-underline',
                      isActive ? 'text-white' : 'text-neutral-400',
                    )
                  }
                >
                  <Icon className="h-5 w-5" />
                  {t.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      ) : null}
    </div>
  );
};

export default PortalLayout;
